import { Component, EventEmitter, Input, Output } from '@angular/core';

import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { NgSelectModule } from '@ng-select/ng-select';

import { TabsComponent, TabItem } from '../tabs/tabs.component';
import { faUpload, faDownload, faTrash, faExclamationTriangle, faCheck } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { Model, ClassificationItem } from '../../services/models.service';
import { ModelsFormComponent } from '../models-form/models-form.component';
import { ClassificationConfigComponent } from '../classification-config/classification-config.component';
import { SimulationModelActionsComponent } from '../simulation-model-actions/simulation-model-actions.component';
import { SimulationModelIoComponent } from '../simulation-model-io/simulation-model-io.component';

@Component({
  selector: 'app-interaction-model-editor',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TabsComponent,
    ModelsFormComponent,
    ClassificationConfigComponent,
    FontAwesomeModule,
    TranslateModule,
    NgSelectModule,
    SimulationModelActionsComponent,
    SimulationModelIoComponent
],
  templateUrl: './interaction-model-editor.component.html',
  styleUrls: ['./interaction-model-editor.component.scss']
})
export class InteractionModelEditorComponent {
  faUpload = faUpload;
  faDownload = faDownload;
  faTrash = faTrash;
  faExclamationTriangle = faExclamationTriangle;
  faCheck = faCheck;

  get hasArchives(): boolean {
    return this.archivesCount > 0;
  }

  private _models: Model[] = [];
  @Input() set models(value: Model[]) {
    this._models = (value || []).slice().sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
  }
  get models(): Model[] {
    return this._models;
  }

  @Input() selectedModel: string | null = null;
  @Input() modelTabs: TabItem[] = [];
  @Input() activeModelTabId: string = 'basic';
  @Input() selectedModelType: 'extraction' | 'classification' = 'extraction';
  @Input() classificationInitial: ClassificationItem[] = [];

  @Input() basicUserListInitial: any = null;
  @Input() generalRulesInitial: any = null;
  @Input() generalQuestionsInitial: any = null;

  @Input() basicUserList: any = null;
  @Input() generalRulesData: any = null;
  @Input() generalQuestionsData: any = null;
  @Input() mentionData: string[] = [];
  @Input() newModelName: string = '';
  @Input() allReferences: any[] = [];
  @Input() selectedReferences: string[] = [];

  @Output() selectedModelChange = new EventEmitter<string | null>();
  @Output() deleteModel = new EventEmitter<string>();
  @Output() activeModelTabIdChange = new EventEmitter<string>();
  @Output() mentionDataChange = new EventEmitter<string[]>();
  @Output() newModelNameChange = new EventEmitter<string>();
  @Output() importModel = new EventEmitter<void>();
  @Output() exportModel = new EventEmitter<void>();
  @Output() saveOnly = new EventEmitter<void>();
  @Output() execute = new EventEmitter<void>();
  @Output() saveAndExecute = new EventEmitter<void>();

  @Input() hasChanges: boolean = false;
  @Input() archivesCount: number = 0;
  @Output() basicFormChange = new EventEmitter<any>();
  @Output() rulesFormChange = new EventEmitter<any>();
  @Output() questionsFormChange = new EventEmitter<any>();
  @Output() referencesChange = new EventEmitter<string[]>();
  @Output() modelHelpClicked = new EventEmitter<string>();
  @Output() classificationFormChange = new EventEmitter<ClassificationItem[]>();

  onClassificationChange(items: ClassificationItem[]): void {
    this.classificationFormChange.emit(items);
  }

  onSelectedModelChange(model: string | null) {
    this.selectedModel = model;
    this.selectedModelChange.emit(model);
  }

  onDeleteModelClick(model: Model, event: Event) {
    event.stopPropagation();
    if (model?.id != null) {
      this.deleteModel.emit(String(model.id));
    }
  }

  onTabChange(tabId: string) {
    this.activeModelTabId = tabId;
    this.activeModelTabIdChange.emit(tabId);
  }

  onNewModelNameChange(name: string) {
    this.newModelName = name;
    this.newModelNameChange.emit(name);
  }

  handleMentionDataChange(ids: string[]) {
    this.mentionDataChange.emit(ids);
  }

  handleBasicFormChange(value: any) {
    this.basicFormChange.emit(value);
  }

  handleRulesFormChange(value: any) {
    this.rulesFormChange.emit(value);
  }

  handleQuestionsFormChange(value: any) {
    this.questionsFormChange.emit(value);
  }
  
  handleReferencesChange(ids: string[]) {
    this.selectedReferences = ids;
    this.referencesChange.emit(ids);
  }

  onImportClick() {
    this.importModel.emit();
  }

  onExportClick() {
    this.exportModel.emit();
  }

  onSaveOnlyClick() {
    this.saveOnly.emit();
  }

  onExecuteClick() {
    if (!this.hasArchives) return;
    this.execute.emit();
  }

  onModelHelpClicked(tabId: string) {
    this.modelHelpClicked.emit(tabId);
  }

  onSaveAndExecuteClick() {
    if (!this.hasArchives) return;
    this.saveAndExecute.emit();
  }
}
